import { SyncJudgeMode } from "$/modules/settings/states";

export interface SyncKeyTiming {
	downTimeOffset: number;
	triggerTime: number;
}

interface CalculateSyncTimeOptions {
	audioTimeSeconds: number;
	playbackRate: number;
	isPlaying: boolean;
	event: SyncKeyTiming;
	judgeMode: SyncJudgeMode;
	syncTimeOffset: number;
	actionOffset?: number;
	performanceTime: number;
	performanceTimeOrigin?: number;
}

function eventTimeOnPerformanceClock(
	eventTime: number,
	performanceTimeOrigin: number,
) {
	return eventTime > 1_000_000_000_000
		? eventTime - performanceTimeOrigin
		: eventTime;
}

export function calculateSyncTime({
	audioTimeSeconds,
	playbackRate,
	isPlaying,
	event,
	judgeMode,
	syncTimeOffset,
	actionOffset = 0,
	performanceTime,
	performanceTimeOrigin = 0,
}: CalculateSyncTimeOptions) {
	// A paused playhead does not move while the key is held or handled, so there
	// is no delay to compensate: every press gives the same time.
	if (!isPlaying) {
		return Math.round(
			Math.max(0, audioTimeSeconds * 1000 + syncTimeOffset + actionOffset),
		);
	}

	const eventTime = eventTimeOnPerformanceClock(
		event.triggerTime,
		performanceTimeOrigin,
	);
	const rawProcessingDelay = performanceTime - eventTime;
	const processingDelay =
		Number.isFinite(rawProcessingDelay) && rawProcessingDelay >= 0
			? rawProcessingDelay
			: 0;
	const audioTimeAtEvent =
		audioTimeSeconds * 1000 - processingDelay * playbackRate;

	let keyAdjustment = 0;
	if (judgeMode === SyncJudgeMode.FirstKeyDownTimeLegacy) {
		keyAdjustment = -event.downTimeOffset;
	} else if (isPlaying) {
		switch (judgeMode) {
			case SyncJudgeMode.FirstKeyDownTime:
				keyAdjustment = -event.downTimeOffset * playbackRate;
				break;
			case SyncJudgeMode.MiddleKeyTime:
				keyAdjustment = -(event.downTimeOffset / 2) * playbackRate;
				break;
		}
	}

	return Math.round(
		Math.max(
			0,
			audioTimeAtEvent + keyAdjustment + syncTimeOffset + actionOffset,
		),
	);
}
